import { Tag } from "@/types";
import { useTranslation } from "react-i18next";

// System tags that appear before user tags
export const SYSTEM_TAGS = {
  ALL: "all",
} as const;

export type SystemTagId = (typeof SYSTEM_TAGS)[keyof typeof SYSTEM_TAGS];

type Props = {
  tags: Tag[];
  selectedTagId: string | null; // null means "all", or can be a system tag id or user tag id
  onSelectTag: (id: string | null) => void;
  className?: string;
};

export default function TagFilterBar({
  tags,
  selectedTagId,
  onSelectTag,
  className = "",
}: Props) {
  const { t } = useTranslation();

  if (tags.length === 0) return null;

  const systemTags: { id: string | null; label: string }[] = [
    { id: null, label: t("tagFilterBar.all") },
  ];

  return (
    <div className={`flex items-center gap-2 overflow-x-auto no-scrollbar px-5 py-3 relative ${className}`}>
      {/* System tags */}
      {systemTags.map((sysTag) => (
        <button
          key={sysTag.id ?? "all"}
          data-action-id="tagFilter.select"
          onClick={() => onSelectTag(sysTag.id)}
          className={`flex-shrink-0 min-h-9 px-4 py-2 rounded-full text-sm font-medium tracking-wide transition-all ${
            selectedTagId === sysTag.id
              ? "bg-[var(--accent)] text-[var(--text-inverse)] shadow-[0_10px_20px_-12px_var(--accent-glow)]"
              : "glass-tile text-[var(--text-secondary)] hover:bg-[var(--glass-bg-hover)]"
          }`}
        >
          {sysTag.label}
        </button>
      ))}

      {/* Separator if there are user tags */}
      {tags.length > 0 && (
        <div className="w-px h-5 bg-[var(--hairline)] mx-1 flex-shrink-0" />
      )}

      {/* User tags */}
      {tags.map((tag) => (
        <button
          key={tag.name}
          data-action-id="tagFilter.select"
          onClick={() => onSelectTag(tag.name)}
          className={`flex-shrink-0 flex items-center gap-1.5 min-h-9 px-4 py-2 rounded-full text-sm font-medium transition-all ${
            selectedTagId === tag.name
              ? "bg-[var(--accent)] text-[var(--text-inverse)] shadow-[0_10px_20px_-12px_var(--accent-glow)]"
              : "glass-tile text-[var(--text-secondary)] hover:bg-[var(--glass-bg-hover)]"
          }`}
        >
          <div
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: tag.color,
              boxShadow: `0 0 4px ${tag.color}40`,
            }}
          />
          {tag.name}
        </button>
      ))}

    </div>
  );
}
